
import React, { useRef, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import {
    isCategory,
    isAllKeyword,
} from '../../../../lib/formulaTokens';
import { lex, TokenType } from '../../../../lib/formulaParser';

interface FormulaEditorProps {
    value: string;
    onChange: (value: string, cursorPosition: number) => void;
    /** Maps ID tokens (ch_xxx, en_xxx, jn_xxx) AND categories to display names */
    tokenToName: Record<string, string>;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
    cursorIndex?: number;
}

export interface FormulaEditorRef {
    getCursorCoordinates: () => { top: number; left: number; height: number } | null;
    focus: (position?: number) => void;
}

export const FormulaEditor = forwardRef<FormulaEditorRef, FormulaEditorProps>(({
    value,
    onChange,
    tokenToName,
    className,
    disabled,
    onKeyDown,
    onFocus,
    onBlur,
    cursorIndex = 0
}, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef<string | null>(null);

    // --- Cursor Restoration Logic ---

    // Helper: Restore cursor to a specific abstract index
    const restoreCursor = (index: number) => {
        if (!editorRef.current) return;

        const range = document.createRange();
        const selection = window.getSelection();
        let charCount = 0;
        let found = false;

        const traverse = (node: Node) => {
            if (found) return;

            if (node.nodeType === Node.TEXT_NODE) {
                const textLength = (node.textContent || '').length;
                if (charCount + textLength >= index) {
                    range.setStart(node, index - charCount);
                    range.collapse(true);
                    found = true;
                } else {
                    charCount += textLength;
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                if (el.hasAttribute('data-value')) {
                    const valLength = (el.getAttribute('data-value') || '').length;
                    if (charCount + valLength >= index) {
                        try {
                            range.setStartAfter(node);
                            range.collapse(true);
                        } catch (e) {
                            // Fallback
                        }
                        found = true;
                    }
                    charCount += valLength;
                } else {
                    for (let i = 0; i < node.childNodes.length; i++) {
                        traverse(node.childNodes[i]);
                        if (found) return;
                    }
                }
            }
        };

        traverse(editorRef.current);

        if (!found && index >= charCount) {
            range.selectNodeContents(editorRef.current);
            range.collapse(false);
        }

        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    useImperativeHandle(ref, () => ({
        getCursorCoordinates: () => {
            const getEditorFallback = () => {
                if (!editorRef.current) return null;
                const rect = editorRef.current.getBoundingClientRect();
                const style = window.getComputedStyle(editorRef.current);
                const pt = parseFloat(style.paddingTop) || 8;
                const pl = parseFloat(style.paddingLeft) || 8;
                return { top: rect.top + pt, left: rect.left + pl, height: 20 };
            };

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) {
                return getEditorFallback();
            }

            const range = selection.getRangeAt(0);

            if (editorRef.current && !editorRef.current.contains(range.commonAncestorContainer)) {
                return getEditorFallback();
            }

            const rect = range.getBoundingClientRect();

            if (rect.width === 0 && rect.height === 0 && rect.x === 0 && rect.y === 0) {
                return getEditorFallback();
            }

            if (rect.height === 0) {
                const fb = getEditorFallback();
                if (fb) return { ...fb, top: rect.top || fb.top, left: rect.left || fb.left };
            }

            return { top: rect.top, left: rect.left, height: rect.height || 20 };
        },
        focus: (position?: number) => {
            if (editorRef.current) {
                editorRef.current.focus();
                if (typeof position === 'number') {
                    restoreCursor(position);
                }
            }
        }
    }));

    // --- HTML Generation (using parser lexer, no regex) ---

    const generateHtml = (formula: string) => {
        if (!formula) return '';

        // Use the deterministic lexer instead of regex
        const { tokens } = lex(formula);

        let html = '';
        let lastEnd = 0;

        for (const tok of tokens) {
            if (tok.type === TokenType.EOF) break;

            // Emit any whitespace or gap between last token end and this token start
            if (tok.start > lastEnd) {
                const gap = formula.substring(lastEnd, tok.start);
                html += gap.replace(/ /g, '\u00A0');
            }

            if (tok.type === TokenType.VARIABLE) {
                // A variable token might be a dotted path like "ch_abc.en_def.revenues"
                // We need to render each segment individually for proper pill rendering
                const segments = tok.value.split('.');
                for (let sIdx = 0; sIdx < segments.length; sIdx++) {
                    if (sIdx > 0) {
                        // Dot separator
                        html += `<span class="text-neutral-400 dark:text-neutral-500 font-bold">.</span>`;
                    }

                    const segment = segments[sIdx];
                    const lowerSeg = segment.toLowerCase();
                    const displayName = tokenToName[segment] || tokenToName[lowerSeg];

                    if (displayName) {
                        let pillColor = "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";

                        if (isCategory(segment)) {
                            if (lowerSeg.includes('revenu')) {
                                pillColor = "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
                            } else if (lowerSeg.includes('expense')) {
                                pillColor = "bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800";
                            }
                        }

                        html += `<span class="${pillColor} rounded px-1.5 py-0.5 mx-0.5 border cursor-default select-none inline-block font-medium text-sm" contenteditable="false" data-value="${segment}">${displayName}</span>`;
                    } else {
                        // Unknown segment
                        const keys = tokenToName ? Object.keys(tokenToName) : [];
                        const isPrefix = keys.some(k => {
                            const idMatch = k.startsWith(segment) || k.startsWith(lowerSeg);
                            if (idMatch) return true;
                            const name = tokenToName[k];
                            return name && name.toLowerCase().startsWith(lowerSeg);
                        });

                        if (isPrefix || isAllKeyword(segment)) {
                            html += segment;
                        } else {
                            html += `<span class="text-rose-500 underline decoration-wavy decoration-rose-500">${segment}</span>`;
                        }
                    }
                }
            } else if (tok.type === TokenType.OPERATOR) {
                html += `<span class="text-neutral-500 dark:text-neutral-400 font-bold mx-0.5">${tok.value}</span>`;
            } else if (tok.type === TokenType.LPAREN || tok.type === TokenType.RPAREN) {
                html += `<span class="text-neutral-500 dark:text-neutral-400 font-bold mx-0.5">${tok.value}</span>`;
            } else if (tok.type === TokenType.NUMBER) {
                html += `<span class="text-amber-600 dark:text-amber-400 font-mono">${tok.value}</span>`;
            }

            lastEnd = tok.end;
        }

        // Trailing content not captured (e.g. trailing dots)
        if (lastEnd < formula.length) {
            html += formula.substring(lastEnd);
        }

        return html;
    };

    // --- Sync External Value to HTML ---

    useLayoutEffect(() => {
        lastValueRef.current = value;
        if (editorRef.current) {
            editorRef.current.innerHTML = generateHtml(value);

            if (typeof cursorIndex === 'number') {
                restoreCursor(cursorIndex);
            }
        }
    }, [value, tokenToName, cursorIndex]);

    // --- Input Handling ---

    const handleInput = () => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        let newValue = '';
        let cursorIndex = 0;

        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(editorRef.current);
        preCaretRange.setEnd(range.endContainer, range.endOffset);

        const extractText = (container: Node): string => {
            let text = '';
            container.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    text += node.textContent || '';
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const el = node as HTMLElement;
                    if (el.hasAttribute('data-value')) {
                        text += el.getAttribute('data-value');
                    } else {
                        text += extractText(node);
                    }
                }
            });
            return text;
        };

        newValue = extractText(editorRef.current);

        const frag = preCaretRange.cloneContents();

        const extractFromFrag = (node: Node): string => {
            let text = '';
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                if (el.hasAttribute('data-value')) {
                    text += el.getAttribute('data-value');
                } else {
                    node.childNodes.forEach(c => text += extractFromFrag(c));
                }
            }
            return text;
        };

        let preCursorText = '';
        frag.childNodes.forEach(n => {
            preCursorText += extractFromFrag(n);
        });

        cursorIndex = preCursorText.length;

        lastValueRef.current = newValue;
        onChange(newValue, cursorIndex);
    };

    return (
        <div
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            className={`min-h-[100px] w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 whitespace-pre-wrap break-words font-mono text-sm leading-6 ${className}`}
            onInput={handleInput}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    if (onKeyDown) onKeyDown(e);
                    if (!e.defaultPrevented) e.preventDefault();
                } else {
                    if (onKeyDown) onKeyDown(e);
                }
            }}
        />
    );
});

FormulaEditor.displayName = 'FormulaEditor';
